import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, RepeatIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { useFeatures } from '../../hooks/useFeatures';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';

/**
 * "Opbouwen": wat er gebeurt nádat je een protocol kiest. Het protocol zelf verkoopt het blok
 * Kiezen erboven (per klacht, met prijs); hier geen tweede rij protocolkaarten, maar de
 * belofte erachter: huidplan, bijsturen, opnieuw kijken. Als tijdlijn, want het is een volgorde;
 * de lijn loopt mee met het scrollen. Eronder het abonnement (sluit aan op "bestel bij").
 * De cadeaustaffel staat hier bewust níét: die geldt voor elke bestelling en staat daarom bij
 * "Liever met één product beginnen?" (HomeShelf).
 */
export function HomeProtocols() {
  const pages = usePages();
  const {
    subscriptions
  } = useFeatures();
  const reduce = useReducedMotion();
  const copy = pages.home.protocols;
  const lineRef = useRef<HTMLOListElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: lineRef,
    offset: ['start 75%', 'end 60%']
  });
  const milestones = copy.milestones.filter(m => m.title);
  return <Section tone="sand" labelledBy="home-protocols" contained={false} data-id="element-7458" data-mp="7srnzewgnjjl">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7459" data-mp="crus8x4m9lqi">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7460" data-mp="crckp4ci7l3a">
          <div className="lg:col-span-5" data-id="element-7461" data-mp="3v52ccazrvrig">
            {copy.eyebrow && <p className={`${eyebrowCls} text-clay`} data-id="element-7462" data-mp="32b7flxcfht0">{fill(copy.eyebrow)}</p>}
            <h2 id="home-protocols" className={`${copy.eyebrow ? 'mt-4' : ''} ${sectionHeading} text-ink`} data-id="element-7463" data-mp="3087oa6e2ab3">
              <HeadingText heading={copy.heading} data-id="element-7464" data-mp="qq2jcoez1str" />
            </h2>
            {copy.body && <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7465" data-mp="vjwzjhvdbpo3">{fill(copy.body)}</p>}
            {copy.cta && <Link to="/routines" className={`mt-6 ${quietLink} text-ink`} data-id="element-7466" data-mp="11d0u0s39bzy9">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7467" data-mp="wdny7huerijj" />
              </Link>}
          </div>

          {milestones.length > 0 && <ol ref={lineRef} className="relative lg:col-span-7 lg:col-start-6 lg:pl-4" data-id="element-7468" data-mp="rh7csvelfia5">
              {/* De lijn van de tijdlijn; vult zich terwijl je leest. Bij "minder beweging" staat hij vol. */}
              <span aria-hidden="true" className="absolute bottom-3 left-[7px] top-3 w-px bg-ink/15 lg:left-[23px]" data-id="element-7469" data-mp="dnfy78m0987k">
                <motion.span style={reduce ? undefined : {
              scaleY: scrollYProgress
            }} className="absolute inset-0 origin-top bg-clay" data-id="element-7470" data-mp="8ov9o6g48cyy" />
              </span>
              {milestones.map((m, i) => <li key={`${i}-${m.title}`} className="relative pb-12 pl-10 last:pb-0 sm:pb-14" data-id="element-7471" data-mp="3uv1m1pa2dcoz" data-mpi={i}>
                  <span aria-hidden="true" className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border border-clay bg-sand lg:left-4" data-id="element-7472" data-mp="3ks4o0qxw2ep2" />
                  <div className="lg:pl-6" data-id="element-7473" data-mp="3pov2xaxyj48w">
                    {m.when && <p className="text-[14px] text-clay" data-id="element-7474" data-mp="3up9fn6ai72d9">{fill(m.when)}</p>}
                    <h3 className="mt-1.5 type-title text-ink" data-id="element-7475" data-mp="3jhnk1d61fnt">{fill(m.title)}</h3>
                    {m.body && <p className="mt-3 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7476" data-mp="3h575sn23dcv">{fill(m.body)}</p>}
                  </div>
                </li>)}
            </ol>}
        </div>

        {/* Het abonnement als één regel: de uitleg zelf staat bij Protocollen en in de webshop. */}
        {subscriptions && <Link to="/routines" className="group mt-16 flex flex-col gap-2 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-7477" data-mp="3gq9rdzqwkphm">
            <span className="flex items-start gap-3 text-[17px] leading-relaxed text-ink" data-id="element-7478" data-mp="2j8t25iiievt1">
              <RepeatIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7479" data-mp="2o7dukvrisb6x" />
              <span data-id="element-7480" data-mp="oufa817nq8vf">
                <span className="type-name" data-id="element-7481" data-mp="ouxdhhsqlncu">{pages.subscription.name}</span>
                <span className="text-muted" data-id="element-7482" data-mp="13jberoai93ra"> · {pages.subscription.claim}</span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 pl-7 text-[15px] text-ink group-hover:underline group-hover:underline-offset-[6px] sm:pl-0" data-id="element-7483" data-mp="ys35qtd37ken">
              Zo werkt het abonnement
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7484" data-mp="18ixrzxqc2k03" />
            </span>
          </Link>}
      </div>
    </Section>;
}